import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  StatusBadge,
  EmptyState,
  useConfirm,
} from "./shared/components";
import {
  getVigilanceInbox,
  getVigilanceProcessed,
  submitVigilanceAction,
  formatDateLong,
  VigilanceCaseRecord,
  VigilanceCaseStatus,
} from "./services/eSeparation.service";

const statusMeta = (s: string) => {
  const u = String(s || "").toUpperCase();
  if (u === "CLEARED") return { label: "Cleared", bg: "#E4F7E8", color: "#049123" };
  if (u === "HOLD") return { label: "Hold", bg: "#F3E5F3", color: "#800080" };
  if (u === "OBJECTION") return { label: "Objection", bg: "#FDE5E5", color: "#C0282C" };
  return { label: "Pending", bg: "#FC9D4520", color: "#BA7517" };
};

const VigilanceClearancePage: React.FC = () => {
  const navigate = useNavigate();
  const routes = all_routes;
  const { confirm, confirmDialog } = useConfirm();

  const [tab, setTab] = useState<"Pending" | "Processed">("Pending");
  const [pending, setPending] = useState<VigilanceCaseRecord[]>([]);
  const [processed, setProcessed] = useState<VigilanceCaseRecord[]>([]);
  const [loading, setLoading] = useState(true);

  const [selected, setSelected] = useState<VigilanceCaseRecord | null>(null);
  const [status, setStatus] = useState<Exclude<VigilanceCaseStatus, "PENDING"> | "">("");
  const [remarks, setRemarks] = useState("");
  const [pendingCaseRef, setPendingCaseRef] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [inbox, done] = await Promise.all([
        getVigilanceInbox(),
        getVigilanceProcessed(),
      ]);
      setPending(inbox.data || []);
      setProcessed(done.data || []);
    } catch (err: any) {
      console.error("Failed to load vigilance cases", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCase = (c: VigilanceCaseRecord) => {
    setSelected(c);
    setStatus("");
    setRemarks("");
    setPendingCaseRef("");
  };

  const closeCase = () => setSelected(null);

  const handleSubmit = async () => {
    if (!selected) return;
    if (!status) return toast.warning("Please select a clearance status.");
    if ((status === "HOLD" || status === "OBJECTION") && !remarks.trim())
      return toast.warning("Please provide remarks for Hold / Objection.");
    if (
      !(await confirm(
        `Mark this case as "${statusMeta(status).label}" for ${selected.emp_name || selected.emp_no || selected.request_id}?`,
        {
          title: "Confirm Clearance Submission",
          confirmLabel: "Submit Clearance",
          tone: status === "CLEARED" ? "success" : "warning",
        }
      ))
    )
      return;
    setSubmitting(true);
    const res = await submitVigilanceAction({
      case_id: selected.id,
      status,
      remarks,
      pending_case_ref: pendingCaseRef,
    });
    setSubmitting(false);
    if (res.success) {
      toast.success("Vigilance clearance recorded successfully.");
      setSelected(null);
      load();
    } else {
      toast.error(res.message || "Failed to submit clearance.");
    }
  };

  const rows = tab === "Pending" ? pending : processed;

  return (
    <div className="page-wrapper">
      {confirmDialog}
      <div className="content pb-5">
        {/* PAGE HEADER */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto">
            <h3 className="page-title fw-bold text-dark mb-1">
              Vigilance Clearance
            </h3>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.Esepration.hub} className="text-decoration-none text-muted">
                    E-Separation
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.Esepration.noDues} className="text-decoration-none text-muted">
                    No Dues
                  </Link>
                </li>
                <li className="breadcrumb-item active text-primary fw-medium" aria-current="page">
                  Vigilance Clearance
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
            <span className="badge bg-purple-light text-purple px-3 py-2 fw-semibold fs-13 d-inline-flex align-items-center gap-1.5">
              <i className="ti ti-shield-check fs-16"></i> Vigilance Authority
            </span>
            <button
              type="button"
              className="btn btn-outline-secondary d-flex align-items-center gap-2 px-3"
              onClick={() => navigate(-1)}
            >
              <i className="ti ti-arrow-left"></i> Back
            </button>
          </div>
        </div>

        {/* TABS HEADER CARD */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-body p-2">
            <div className="nav nav-pills nav-fill gap-2" role="tablist">
              <button
                type="button"
                className={`nav-link py-2.5 px-4 fw-semibold d-flex align-items-center justify-content-center gap-2 ${
                  tab === "Pending" ? "active bg-primary text-white" : "text-muted bg-light"
                }`}
                style={{ borderRadius: 8, transition: "all 0.2s ease" }}
                onClick={() => {
                  setTab("Pending");
                  setSelected(null);
                }}
              >
                <i className="ti ti-clock"></i>
                Pending Cases
                <span className={`badge ms-1 ${tab === "Pending" ? "bg-white text-primary" : "bg-secondary text-white"}`}>
                  {pending.length}
                </span>
              </button>
              <button
                type="button"
                className={`nav-link py-2.5 px-4 fw-semibold d-flex align-items-center justify-content-center gap-2 ${
                  tab === "Processed" ? "active bg-primary text-white" : "text-muted bg-light"
                }`}
                style={{ borderRadius: 8, transition: "all 0.2s ease" }}
                onClick={() => {
                  setTab("Processed");
                  setSelected(null);
                }}
              >
                <i className="ti ti-circle-check"></i>
                Processed Cases
                <span className={`badge ms-1 ${tab === "Processed" ? "bg-white text-primary" : "bg-secondary text-white"}`}>
                  {processed.length}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* CASES LIST SECTION */}
        {loading ? (
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
            <div className="card-body text-center py-5">
              <div className="spinner-border text-primary" role="status" />
              <p className="text-muted mt-3 mb-0">Loading vigilance cases...</p>
            </div>
          </div>
        ) : rows.length === 0 ? (
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
            <div className="card-body py-5">
              <EmptyState
                icon="ti-shield-check"
                message={
                  tab === "Pending"
                    ? "No pending vigilance cases on your desk."
                    : "No processed vigilance cases yet."
                }
              />
            </div>
          </div>
        ) : (
          <div className="row g-3 mb-4">
            {rows.map((c) => {
              const meta = statusMeta(c.status);
              const isSelected = selected?.id === c.id;
              const initials = (c.emp_name || "EM")
                .split(" ")
                .map((n) => n[0])
                .slice(0, 2)
                .join("")
                .toUpperCase();

              return (
                <div className="col-md-6 col-xl-4" key={c.id}>
                  <div
                    className="card border h-100 position-relative transition-all"
                    style={{
                      borderRadius: 12,
                      cursor: "pointer",
                      borderColor: isSelected ? "#0d6efd" : "#e9ecef",
                      background: isSelected ? "#f0f7ff" : "#fff",
                      boxShadow: isSelected ? "0 4px 12px rgba(13,110,253,0.12)" : "0 1px 3px rgba(0,0,0,0.05)",
                      transition: "all 0.2s ease-in-out",
                    }}
                    onClick={() => openCase(c)}
                  >
                    <div className="card-body p-3.5 d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start mb-2.5">
                        <div className="d-flex align-items-center gap-2.5 overflow-hidden">
                          <span
                            className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0 fw-bold"
                            style={{
                              width: 38,
                              height: 38,
                              background: isSelected ? "#0d6efd" : "#e9ecef",
                              color: isSelected ? "#fff" : "#495057",
                              fontSize: 13,
                            }}
                          >
                            {initials}
                          </span>
                          <div className="overflow-hidden">
                            <h6 className="mb-0 fw-bold text-dark text-truncate" style={{ fontSize: 14 }}>
                              {c.emp_name || c.emp_no || c.request_id}
                            </h6>
                            <small className="text-muted d-block text-truncate">
                              Emp No: <span className="fw-semibold text-dark">{c.emp_no || "—"}</span>
                            </small>
                          </div>
                        </div>
                        <span
                          className="badge flex-shrink-0"
                          style={{ background: meta.bg, color: meta.color, fontSize: 11, padding: "4px 8px" }}
                        >
                          {meta.label}
                        </span>
                      </div>

                      <div className="bg-light rounded-2 p-2 mb-2 small text-muted">
                        <div className="d-flex justify-content-between mb-1">
                          <span>Request ID:</span>
                          <span className="fw-semibold text-dark">{c.request_id}</span>
                        </div>
                        {c.dispatched_at && (
                          <div className="d-flex justify-content-between">
                            <span>Dispatched:</span>
                            <span className="fw-medium text-dark">{String(c.dispatched_at).slice(0, 10)}</span>
                          </div>
                        )}
                      </div>

                      <div className="mt-auto pt-2 border-top d-flex align-items-center justify-content-between small text-muted">
                        <span className="fw-medium text-primary">
                          {tab === "Pending" ? "Tap to review & clear" : "Tap to view details"}
                        </span>
                        <i className={`ti ${isSelected ? "ti-check text-primary" : "ti-chevron-right"}`}></i>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* SELECTED CASE DETAIL CARDS */}
        {selected && (
          <div className="mt-4">
            {/* SECTION 01: CASE DETAILS */}
            <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
              <div className="card-header bg-white border-bottom py-3 px-4">
                <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                  <span
                    className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                    style={{ width: 34, height: 34, fontSize: 15 }}
                  >
                    01
                  </span>
                  Case Details
                </h5>
              </div>
              <div className="card-body p-4">
                <div className="row g-3">
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Employee Name
                    </label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={selected.emp_name || "—"}
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Employee No.
                    </label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={selected.emp_no || "—"}
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Request ID
                    </label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={selected.request_id || "—"}
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Sent On
                    </label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={selected.dispatched_at ? formatDateLong(selected.dispatched_at) : "—"}
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Status
                    </label>
                    <div className="form-control bg-light d-flex align-items-center">
                      <StatusBadge status={selected.status} />
                    </div>
                  </div>

                  {tab === "Processed" && (
                    <>
                      {selected.acted_at && (
                        <div className="col-lg-3 col-md-4 col-sm-6">
                          <label className="form-label fw-semibold text-secondary small mb-1">
                            Actioned On
                          </label>
                          <input
                            type="text"
                            className="form-control bg-light"
                            value={formatDateLong(selected.acted_at)}
                            readOnly
                          />
                        </div>
                      )}
                      {selected.pending_case_ref && (
                        <div className="col-lg-3 col-md-4 col-sm-6">
                          <label className="form-label fw-semibold text-secondary small mb-1">
                            Pending Case Ref.
                          </label>
                          <input
                            type="text"
                            className="form-control bg-light"
                            value={selected.pending_case_ref}
                            readOnly
                          />
                        </div>
                      )}
                      {selected.remarks && (
                        <div className="col-12">
                          <label className="form-label fw-semibold text-secondary small mb-1">
                            Remarks
                          </label>
                          <textarea
                            className="form-control bg-light"
                            rows={2}
                            value={selected.remarks}
                            readOnly
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* SECTION 02: CLEARANCE DECISION (FOR PENDING CASES) */}
            {tab === "Pending" && (
              <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
                <div className="card-header bg-white border-bottom py-3 px-4">
                  <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                    <span
                      className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                      style={{ width: 34, height: 34, fontSize: 15 }}
                    >
                      02
                    </span>
                    Clearance Decision
                  </h5>
                </div>
                <div className="card-body p-4">
                  {/* Status selection cards */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold text-secondary small mb-2">
                      Clearance Status <span className="text-danger">*</span>
                    </label>
                    <div className="row g-3">
                      {(
                        [
                          {
                            key: "CLEARED",
                            label: "Cleared",
                            icon: "ti-circle-check",
                            color: "#049123",
                            desc: "No vigilance pending / Disciplinary cleared",
                          },
                          {
                            key: "HOLD",
                            label: "Hold",
                            icon: "ti-player-pause",
                            color: "#800080",
                            desc: "Case under active inquiry / Hold clearance",
                          },
                          {
                            key: "OBJECTION",
                            label: "Objection",
                            icon: "ti-alert-triangle",
                            color: "#C0282C",
                            desc: "Vigilance objection / Dues / Case registered",
                          },
                        ] as const
                      ).map((opt) => {
                        const isChosen = status === opt.key;
                        return (
                          <div className="col-md-4" key={opt.key}>
                            <button
                              type="button"
                              className="btn w-100 text-start p-3.5 h-100 position-relative"
                              style={{
                                borderRadius: 10,
                                border: `2px solid ${isChosen ? opt.color : "#e9ecef"}`,
                                background: isChosen ? `${opt.color}0D` : "#fff",
                                transition: "all 0.2s ease-in-out",
                              }}
                              onClick={() => setStatus(opt.key)}
                            >
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <span
                                  className="d-inline-flex align-items-center justify-content-center rounded-3 p-2"
                                  style={{
                                    background: isChosen ? opt.color : "#f8f9fa",
                                    color: isChosen ? "#fff" : opt.color,
                                  }}
                                >
                                  <i className={`ti ${opt.icon} fs-5`} />
                                </span>
                                {isChosen && (
                                  <span
                                    className="badge rounded-circle p-1"
                                    style={{ background: opt.color, color: "#fff" }}
                                  >
                                    <i className="ti ti-check" />
                                  </span>
                                )}
                              </div>
                              <div
                                className="fw-bold"
                                style={{ color: isChosen ? opt.color : "#212529", fontSize: 14 }}
                              >
                                {opt.label}
                              </div>
                              <small className="text-muted d-block mt-1" style={{ fontSize: 11.5 }}>
                                {opt.desc}
                              </small>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Remarks */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Remarks {(status === "HOLD" || status === "OBJECTION") && <span className="text-danger">*</span>}
                    </label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Enter remarks explaining clearance decision / details..."
                    />
                  </div>

                  {/* Pending case reference */}
                  <div className="mb-2">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Pending Case Reference <span className="text-muted fw-normal">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={pendingCaseRef}
                      onChange={(e) => setPendingCaseRef(e.target.value)}
                      placeholder="e.g. VIG/2026/00123"
                    />
                  </div>
                </div>

                <div className="card-footer bg-white border-top p-3 d-flex justify-content-end gap-2">
                  <button type="button" className="btn btn-outline-secondary px-4" onClick={closeCase}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary px-4 d-inline-flex align-items-center gap-2"
                    onClick={handleSubmit}
                    disabled={!status || submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-send" />
                        Submit Clearance
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {tab === "Processed" && (
              <div className="d-flex justify-content-end mb-4">
                <button type="button" className="btn btn-outline-secondary px-4" onClick={closeCase}>
                  Close Details
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VigilanceClearancePage;
